import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import moment from "moment";
// Constants - Functions
import { WEB_SERVICE_MIMS, errorColor, bodyColor } from "../../../../../config/constants";
import { isValidTime } from "../../../../../config/functions";
import {
  localizationMapping,
  RASSValues,
  optionValues,
  positionValues,
  deliriumValues,
  mechanicalVentilationValues,
  mobilityValues,
} from "../../../functions";
// Components
import {
  EvaluationDataDiv,
  TitleDiv,
  Title,
  DataDiv,
  ColumnDataDiv,
  InputDataDiv,
  Label,
  EvaluationDateInput,
  Textarea,
  ManagementDiv,
  SaveAndCancelDiv,
  SaveButton,
  CancelButton,
  DeleteButton,
} from "./styles";
import Select from "../../../../selectInput";
import UseFetch from "../../../../../config/useFetch";
import Popup from "../../../../popup";
import DeleteEvaluation from "../deleteEvaluation";
// Assets
import SelectArrow from "../../../../../assets/icons/ico_arrow_down.svg";

const EvaluationForm = ({
  type = "create",
  evaluation,
  evaluationIndex,
  evaluationDate,
  evaluationTime,
  setEvaluationTime,
  evaluationTimeError,
  setEvaluationTimeError,
  RASS,
  setRASS,
  RASSError,
  setRASSError,
  Dyspnoea,
  setDyspnoea,
  DyspnoeaError,
  setDyspnoeaError,
  Pain,
  setPain,
  PainError,
  setPainError,
  AnxiousSymptomatology,
  setAnxiousSymptomatology,
  AnxiousSymptomatologyError,
  setAnxiousSymptomatologyError,
  DepressiveSymptoms,
  setDepressiveSymptoms,
  DepressiveSymptomsError,
  setDepressiveSymptomsError,
  Delirium,
  setDelirium,
  MechanicalVentilation,
  setMechanicalVentilation,
  Position,
  setPosition,
  Sleep,
  setSleep,
  SleepError,
  setSleepError,
  Mobility,
  setMobility,
  Observations,
  setObservations,
  setIsShowingEvaluationPopup,
  recharge,
  setRecharge,
}) => {
  const [isShowDeleteEvaluationPopup, setIsShowDeleteEvaluationPopup] = useState(false);
  const { patientId } = useParams();
  const [t] = useTranslation("global");

  const saveEvaluation = () => {
    if (
      evaluationTime === "" ||
      RASS === "" ||
      Dyspnoea === "" ||
      Pain === "" ||
      AnxiousSymptomatology === "" ||
      DepressiveSymptoms === "" ||
      Sleep === ""
    ) {
      toast.error(t("emptyDataMessage"));
      if (evaluationTime === "") setEvaluationTimeError(true);
      if (RASS === "") setRASSError(true);
      if (Dyspnoea === "") setDyspnoeaError(true);
      if (Pain === "") setPainError(true);
      if (AnxiousSymptomatology === "") setAnxiousSymptomatologyError(true);
      if (DepressiveSymptoms === "") setDepressiveSymptomsError(true);
      if (Sleep === "") setSleepError(true);
      return;
    }

    if (!isValidTime(evaluationTime)) {
      toast.error(t("invalidTimeMessage"));
      setEvaluationTimeError(true);
      return;
    }

    let evaluationData = {
      evaluationDate: `${evaluationDate} ${evaluationTime}`,
      evaluationKeys: {
        RASS,
        Dyspnoea,
        Pain,
        AnxiousSymptomatology,
        DepressiveSymptoms,
        Delirium,
        MechanicalVentilation,
        Position,
        Sleep,
        Mobility,
        Observations,
      },
    };

    if (type === "create") {
      UseFetch("post", `${WEB_SERVICE_MIMS}/icu/add-evaluation/${patientId}`, new AbortController(), evaluationData)
        .then(({ data }) => {
          if (data.evaluationAdded) {
            toast.success(t("evaluationCreatedMessage"));
            setIsShowingEvaluationPopup(false);
            setRecharge(!recharge);
          }
        })
        .catch((error) => {
          toast.error(t("sendDataErrorMessage"));
          console.log("Add evaluation: ", error);
        });
    }

    if (type === "update") {
      evaluationData.evaluationUnit = evaluation.EVALUATION_UNIT;
      UseFetch("put", `${WEB_SERVICE_MIMS}/icu/update-evaluation/${patientId}`, new AbortController(), evaluationData)
        .then(({ data }) => {
          if (data.evaluationUpdated) {
            toast.success(t("evaluationUpdatedMessage"));
            setIsShowingEvaluationPopup(false);
            setRecharge(!recharge);
          }
        })
        .catch((error) => {
          toast.error(t("sendDataErrorMessage"));
          console.log("Update evaluation: ", error);
        });
    }
  };

  const handleSetInput = (e, setInput, setError) => {
    setInput(e.target.value);
    setError(false);
  };

  const renderSelectWithError = (text, value, setValue, error, setError, arrayValues) => {
    return (
      <InputDataDiv>
        <Label>{t(text)}</Label>
        <Select
          id={text}
          value={value}
          onChange={(e) => handleSetInput(e, setValue, setError)}
          padding="5px 35px 5px 10px"
          width="190px"
          arrowImg={SelectArrow}
          backgroundPosition="165px center"
          error={error}
        >
          <option value=""></option>
          {arrayValues.map((value) => {
            return (
              <option key={value} value={value}>
                {value}
              </option>
            );
          })}
        </Select>
      </InputDataDiv>
    );
  };

  const renderSelect = (text, value, setValue, arrayValues) => {
    return (
      <InputDataDiv>
        <Label>{t(text)}</Label>
        <Select
          id={text}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          padding="5px 35px 5px 10px"
          width="190px"
          arrowImg={SelectArrow}
          backgroundPosition="165px center"
        >
          <option value="-">-</option>
          {arrayValues.map((value) => {
            return (
              <option key={value} value={value}>
                {t(localizationMapping(value))}
              </option>
            );
          })}
        </Select>
      </InputDataDiv>
    );
  };

  const renderActionButtons = () => {
    return (
      <ManagementDiv justifyContent={type === "update" ? "space-between" : "center"}>
        <SaveAndCancelDiv>
          <SaveButton onClick={saveEvaluation}>{t("save")}</SaveButton>
          <CancelButton onClick={() => setIsShowingEvaluationPopup(false)}>{t("cancel")}</CancelButton>
        </SaveAndCancelDiv>
        {type === "update" && (
          <>
            <DeleteButton onClick={() => setIsShowDeleteEvaluationPopup(true)}>{t("delete")}</DeleteButton>
            <Popup isShowed={isShowDeleteEvaluationPopup} setIsShowed={setIsShowDeleteEvaluationPopup}>
              <DeleteEvaluation
                setIsShowDeleteEvaluationPopup={setIsShowDeleteEvaluationPopup}
                setIsShowingEvaluationPopup={setIsShowingEvaluationPopup}
                patientId={patientId}
                evaluationUnit={evaluation.EVALUATION_UNIT}
                recharge={recharge}
                setRecharge={setRecharge}
              />
            </Popup>
          </>
        )}
      </ManagementDiv>
    );
  };

  return (
    <EvaluationDataDiv>
      <TitleDiv>
        <Title>{`${t("evaluation")}${type === "update" ? ` ${evaluationIndex}` : ""}`}</Title>
      </TitleDiv>
      <DataDiv>
        <ColumnDataDiv width="385px">
          {/* Evaluation date */}
          <InputDataDiv>
            <Label>{t("date")}</Label>
            <EvaluationDateInput
              format="##/##/####"
              placeholder="dd/mm/yyyy"
              mask={["d", "d", "m", "m", "y", "y", "y", "y"]}
              value={moment(evaluationDate, "YYYY-MM-DD").format("DD/MM/YYYY")}
              border={`1px solid ${bodyColor}`}
              disabled
            />
          </InputDataDiv>
          {/* RASS */}
          {renderSelectWithError("RASS", RASS, setRASS, RASSError, setRASSError, RASSValues)}
          {/* Dyspnoea */}
          {renderSelectWithError("Dyspnoea", Dyspnoea, setDyspnoea, DyspnoeaError, setDyspnoeaError, optionValues)}
          {/* Anxious Symptomatology */}
          {renderSelectWithError(
            "AnxiousSymptomatology",
            AnxiousSymptomatology,
            setAnxiousSymptomatology,
            AnxiousSymptomatologyError,
            setAnxiousSymptomatologyError,
            optionValues
          )}
          {/* Sleep */}
          {renderSelectWithError("Sleep", Sleep, setSleep, SleepError, setSleepError, optionValues)}
          {/* Position */}
          {renderSelect("Position", Position, setPosition, positionValues)}
          {/* Observations */}
          <InputDataDiv alignItems="flex-start">
            <Label htmlFor={Observations}>{t("Observations")}</Label>
            <Textarea id={Observations} value={Observations} onChange={(e) => setObservations(e.target.value)} />
          </InputDataDiv>
        </ColumnDataDiv>
        <ColumnDataDiv width="385px">
          {/* Evaluation time */}
          <InputDataDiv>
            <Label>{t("time")}</Label>
            <EvaluationDateInput
              format="##:##"
              placeholder="hh:mm"
              value={evaluationTime}
              onChange={(e) => handleSetInput(e, setEvaluationTime, setEvaluationTimeError)}
              border={evaluationTimeError ? `1px solid ${errorColor}` : `1px solid ${bodyColor}`}
            />
          </InputDataDiv>
          {/* Delirium */}
          {renderSelect("Delirium", Delirium, setDelirium, deliriumValues)}
          {/* Pain */}
          {renderSelectWithError("Pain", Pain, setPain, PainError, setPainError, optionValues)}
          {/* DepressiveSymptoms */}
          {renderSelectWithError(
            "DepressiveSymptoms",
            DepressiveSymptoms,
            setDepressiveSymptoms,
            DepressiveSymptomsError,
            setDepressiveSymptomsError,
            optionValues
          )}
          {/* Mechanical Ventilation */}
          {renderSelect("MechanicalVentilation", MechanicalVentilation, setMechanicalVentilation, mechanicalVentilationValues)}
          {/* Mobility */}
          {renderSelect("Mobility", Mobility, setMobility, mobilityValues)}
        </ColumnDataDiv>
      </DataDiv>
      {renderActionButtons()}
    </EvaluationDataDiv>
  );
};

export default EvaluationForm;
